import React from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertColor } from "@mui/material/Alert";

type ToastMessage = {
  key: number;
  message: string;
  severity?: AlertColor;
  autoHideDuration?: number;
};

type ToastContextType = {
  showToast: (message: string, options?: { severity?: AlertColor; autoHideDuration?: number }) => void;
};

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast deve ser usado dentro de ToastProvider");
  }
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = React.useState<ToastMessage[]>([]);
  const [current, setCurrent] = React.useState<ToastMessage | null>(null);
  const [open, setOpen] = React.useState(false);

  const processQueue = React.useCallback(() => {
    if (current || queue.length === 0) return;
    const next = queue[0];
    setCurrent(next);
    setOpen(true);
  }, [current, queue]);

  React.useEffect(() => {
    processQueue();
  }, [queue, processQueue]);

  const showToast = (message: string, options?: { severity?: AlertColor; autoHideDuration?: number }) => {
    const item: ToastMessage = {
      key: Date.now() + Math.random(),
      message,
      severity: options?.severity || "success",
      autoHideDuration: options?.autoHideDuration ?? 3500,
    };
    setQueue((q) => [...q, item]);
  };

  const handleClose = (_?: any, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const handleExited = () => {
    setCurrent(null);
    setQueue((q) => q.slice(1));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        key={current?.key}
        open={open}
        autoHideDuration={current?.autoHideDuration}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert elevation={6} variant="filled" severity={current?.severity || "success"} onClose={handleClose}>
          {current?.message}
        </MuiAlert>
      </Snackbar>
    </ToastContext.Provider>
  );
}